/**
 * Admin: free IMData upload (bypasses Stripe).
 *
 * Lets the admin run disconnect, reverse, or both against an uploaded CSV/XLSX
 * and/or against previously-verified good phones from the cross-tenant
 * phoneRegistry. The backend never charges and never asks for checkout. Bad
 * phones from the cross-tenant cache are dropped automatically before submit.
 */

import { useState, ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';
import { PortalHeader } from '../Layout/PortalHeader';
import * as XLSX from 'xlsx';

type RunType = 'disconnect' | 'reverse' | 'disconnect_plus_reverse';
type RegistryRerun = 'none' | 'disconnect' | 'reverse' | 'both';

interface ColumnMapping {
  phone?: number;
  firstName?: number;
  lastName?: number;
  fullName?: number;
  email?: number;
  address?: number;
  city?: number;
  state?: number;
  zip?: number;
}

const COLUMN_ALIASES: Record<keyof ColumnMapping, string[]> = {
  phone: ['phone', 'phone number', 'phone_number', 'cell', 'mobile', 'tel', 'telephone', 'cell phone'],
  firstName: ['first name', 'first_name', 'firstname', 'fname', 'first'],
  lastName: ['last name', 'last_name', 'lastname', 'lname', 'last', 'surname'],
  fullName: ['full name', 'full_name', 'fullname', 'name', 'contact'],
  email: ['email', 'email address', 'e-mail', 'mail'],
  address: ['address', 'address1', 'address 1', 'street', 'mailing address'],
  city: ['city', 'town', 'mailing city'],
  state: ['state', 'state code', 'province'],
  zip: ['zip', 'zip code', 'zipcode', 'postal', 'postal code'],
};

function parseCSV(text: string): string[][] {
  const lines = text.split('\n');
  return lines.map(line => {
    const result: string[] = [];
    let cur = '';
    let inQ = false;
    for (const ch of line) {
      if (ch === '"') inQ = !inQ;
      else if (ch === ',' && !inQ) { result.push(cur.trim()); cur = ''; }
      else cur += ch;
    }
    result.push(cur.trim());
    return result;
  }).filter(r => r.some(c => c));
}

async function parseExcel(file: File): Promise<string[][]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows: string[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' }) as string[][];
        resolve(rows.filter(r => r.some(c => c && String(c).trim()))
          .map(r => r.map(c => String(c ?? '').trim())));
      } catch (err) { reject(err); }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

function autoMap(headers: string[]): ColumnMapping {
  const m: ColumnMapping = {};
  const used = new Set<number>();
  for (const field of Object.keys(COLUMN_ALIASES) as (keyof ColumnMapping)[]) {
    const aliases = COLUMN_ALIASES[field];
    const idx = headers.findIndex((h, i) => {
      if (used.has(i)) return false;
      const norm = h.toLowerCase().replace(/[\s_]+/g, ' ').trim();
      return aliases.includes(norm);
    });
    if (idx !== -1) { m[field] = idx; used.add(idx); }
  }
  return m;
}

const TOLL_FREE = new Set(['800', '833', '844', '855', '866', '877', '888']);
function cleanPhone10(raw: string): string {
  let d = (raw || '').replace(/\D/g, '');
  if (d.length === 11 && d[0] === '1') d = d.slice(1);
  if (d.length !== 10) return '';
  if (/(\d)\1{4,}/.test(d)) return '';
  if (/[01]/.test(d[0])) return '';
  if (d[1] === '1' && d[2] === '1') return '';
  if (TOLL_FREE.has(d.slice(0, 3))) return '';
  if (/[01]/.test(d[3])) return '';
  return d;
}

function splitFullName(s: string): { firstName: string; lastName: string } {
  if (!s?.trim()) return { firstName: '', lastName: '' };
  const t = s.trim().replace(/\s+/g, ' ');
  if (t.includes(',')) {
    const [last, first] = t.split(',').map(p => p.trim());
    return { firstName: first || '', lastName: last || '' };
  }
  const w = t.split(' ');
  if (w.length === 1) return { firstName: w[0], lastName: '' };
  return { firstName: w[0], lastName: w.slice(1).join(' ') };
}

export function AdminImdata() {
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [allRows, setAllRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [runType, setRunType] = useState<RunType>('disconnect');
  const [cellOnly, setCellOnly] = useState(false);
  const [rerun, setRerun] = useState<RegistryRerun>('none');
  const [registryLimit, setRegistryLimit] = useState<number>(50000);

  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resultJobId, setResultJobId] = useState<string | null>(null);

  const handleFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setError(null);
    setStatus(null);
    try {
      const ext = f.name.toLowerCase().split('.').pop();
      let rows: string[][];
      if (ext === 'csv' || ext === 'txt') rows = parseCSV(await f.text());
      else if (ext === 'xls' || ext === 'xlsx') rows = await parseExcel(f);
      else { setError('Unsupported file type — use .csv, .xls, or .xlsx'); return; }
      if (rows.length < 2) { setError('File needs a header + data rows'); return; }
      setHeaders(rows[0]);
      setAllRows(rows.slice(1));
      setMapping(autoMap(rows[0]));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to read file');
    }
  };

  const clearFile = () => {
    setFile(null);
    setHeaders([]);
    setAllRows([]);
    setMapping({});
  };

  const needsName = runType === 'reverse' || runType === 'disconnect_plus_reverse';

  const submit = async () => {
    setError(null);
    setStatus(null);
    setResultJobId(null);

    if (!file && rerun === 'none') {
      setError('Upload a file, enable registry rerun, or both.');
      return;
    }

    // Build phones + leadRows from upload
    const phones: string[] = [];
    const leadRows: Array<{
      phone: string; firstName: string; lastName: string; email: string;
      address: string; city: string; state: string; zip: string;
    }> = [];

    if (file) {
      if (mapping.phone === undefined) { setError('Map the Phone column.'); return; }
      if (needsName && mapping.firstName === undefined && mapping.fullName === undefined) {
        setError('Reverse runs need First Name (or a Full Name column).');
        return;
      }
      const seen = new Set<string>();
      for (const row of allRows) {
        const p = cleanPhone10(row[mapping.phone] || '');
        if (!p || seen.has(p)) continue;
        seen.add(p);
        let firstName = '', lastName = '';
        if (mapping.fullName !== undefined) {
          const split = splitFullName(row[mapping.fullName] || '');
          firstName = split.firstName; lastName = split.lastName;
        }
        if (mapping.firstName !== undefined && row[mapping.firstName]) firstName = row[mapping.firstName].trim();
        if (mapping.lastName !== undefined && row[mapping.lastName]) lastName = row[mapping.lastName].trim();
        phones.push(p);
        leadRows.push({
          phone: p, firstName, lastName,
          email: mapping.email !== undefined ? (row[mapping.email] || '').trim() : '',
          address: mapping.address !== undefined ? (row[mapping.address] || '').trim() : '',
          city: mapping.city !== undefined ? (row[mapping.city] || '').trim() : '',
          state: mapping.state !== undefined ? (row[mapping.state] || '').trim() : '',
          zip: mapping.zip !== undefined ? (row[mapping.zip] || '').trim() : '',
        });
      }
      if (phones.length === 0 && rerun === 'none') {
        setError('No valid phones found in upload.');
        return;
      }
    }

    setSubmitting(true);
    setStatus('Submitting to IMData (no charge)...');
    try {
      const fn = httpsCallable<
        {
          phones: string[];
          leadRows: typeof leadRows;
          runType: RunType;
          cellOnly: boolean;
          includeRegistryRerun: RegistryRerun;
          registryLimit: number;
        },
        {
          jobId: string;
          jobIds: string[];
          totalPhones: number;
          fromUpload: number;
          fromRegistry: number;
          droppedBad: number;
          runType: RunType;
          chunkCount: number;
          chunkSize: number;
        }
      >(functions, 'createAdminImdataJob');
      const res = await fn({
        phones, leadRows, runType, cellOnly,
        includeRegistryRerun: rerun, registryLimit,
      });
      setResultJobId(res.data.jobId);
      setStatus(
        `${res.data.chunkCount} job${res.data.chunkCount === 1 ? '' : 's'} created ` +
        `(${res.data.chunkSize.toLocaleString()}/job) — ` +
        `${res.data.totalPhones.toLocaleString()} phones submitted ` +
        `(upload: ${res.data.fromUpload.toLocaleString()}, ` +
        `registry: ${res.data.fromRegistry.toLocaleString()}, ` +
        `bad-cache dropped: ${res.data.droppedBad.toLocaleString()}). ` +
        `Watch progress on the Verification Jobs page.`
      );
      // Reset upload but keep run config for repeat runs
      clearFile();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PortalHeader showCredits={false} />

      <div className="container" style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingTop: '16px', paddingBottom: 0 }}>
        <Link to="/admin" className="back-link">Back</Link>
        <h1 style={{ margin: 0, fontSize: '1.2rem' }}>IMData Upload (free, admin only)</h1>
      </div>

      <div className="container">
        <div style={{
          padding: '14px 18px', marginBottom: '20px', borderRadius: '8px',
          background: 'rgba(200, 168, 78, 0.08)', border: '1px solid rgba(200, 168, 78, 0.3)',
          color: 'var(--gray-200)', fontSize: '0.9rem', lineHeight: 1.6,
        }}>
          <strong style={{ color: 'var(--gold)' }}>Admin path</strong> — these runs bypass Stripe entirely.
          Upload a CSV/XLSX, or rerun previously-verified good phones from the cross-tenant <code>phoneRegistry</code>,
          or both. Bad-cache phones are dropped automatically before submit. Results land on the
          <Link to="/admin/verification-jobs" style={{ color: 'var(--gold)', marginLeft: 6 }}>Verification Jobs</Link> page once complete.
        </div>

        {/* Run type */}
        <h2 className="section-title">Run type</h2>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
          {[
            { id: 'disconnect', label: 'Disconnect only' },
            { id: 'reverse', label: 'Reverse only' },
            { id: 'disconnect_plus_reverse', label: 'Both (disc → reverse)' },
          ].map(opt => (
            <label key={opt.id} style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '10px 14px', borderRadius: '8px', cursor: 'pointer',
              background: runType === opt.id ? 'rgba(200, 168, 78, 0.18)' : 'rgba(255,255,255,0.03)',
              border: `1px solid ${runType === opt.id ? 'var(--gold)' : 'rgba(255,255,255,0.1)'}`,
            }}>
              <input type="radio" name="runType" checked={runType === opt.id}
                onChange={() => setRunType(opt.id as RunType)} disabled={submitting} />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', cursor: 'pointer' }}>
          <input type="checkbox" checked={cellOnly}
            onChange={e => setCellOnly(e.target.checked)} disabled={submitting} />
          <span>Cell phones only (drops landline/voip during reverse stage)</span>
        </label>

        {/* Registry rerun */}
        <h2 className="section-title">Rerun previously verified good phones</h2>
        <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem', marginBottom: '12px' }}>
          Pulls good phones from the cross-tenant <code>phoneRegistry</code>. Bad phones are excluded by design.
          Up to {registryLimit.toLocaleString()} phones per job.
        </p>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
          {[
            { id: 'none', label: 'No rerun' },
            { id: 'disconnect', label: 'Rerun disconnect' },
            { id: 'reverse', label: 'Rerun reverse' },
            { id: 'both', label: 'Rerun both' },
          ].map(opt => (
            <label key={opt.id} style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '8px 12px', borderRadius: '8px', cursor: 'pointer',
              background: rerun === opt.id ? 'rgba(46,163,242,0.15)' : 'rgba(255,255,255,0.03)',
              border: `1px solid ${rerun === opt.id ? 'rgba(46,163,242,0.6)' : 'rgba(255,255,255,0.1)'}`,
            }}>
              <input type="radio" name="rerun" checked={rerun === opt.id}
                onChange={() => setRerun(opt.id as RegistryRerun)} disabled={submitting} />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
        {rerun !== 'none' && (
          <div style={{ marginBottom: '24px' }}>
            <label style={{ fontSize: '0.85rem', color: 'var(--gray-300)' }}>
              Registry phone cap (max 50,000):&nbsp;
              <input type="number" min={1} max={50000} value={registryLimit}
                onChange={e => setRegistryLimit(Math.max(1, Math.min(50000, parseInt(e.target.value) || 1)))}
                disabled={submitting}
                style={{ width: '120px', padding: '4px 8px' }} />
            </label>
          </div>
        )}

        {/* File upload */}
        <h2 className="section-title">Upload file (optional)</h2>
        {!file ? (
          <label className="upload-area" style={{ marginBottom: '24px' }}>
            <input type="file" accept=".csv,.xls,.xlsx,.txt" onChange={handleFile} />
            <div>
              <div style={{ fontSize: '1.1rem', color: 'var(--gold)' }}>Click to select file</div>
              <div style={{ color: 'var(--gray-400)', fontSize: '0.85rem' }}>CSV, XLS, XLSX</div>
            </div>
          </label>
        ) : (
          <div style={{ marginBottom: '24px' }}>
            <p style={{ color: 'var(--gray-300)', fontSize: '0.9rem' }}>
              {file.name} · {allRows.length.toLocaleString()} rows
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px', margin: '12px 0' }}>
              {(['phone', 'firstName', 'lastName', 'fullName', 'email', 'address', 'city', 'state', 'zip'] as (keyof ColumnMapping)[]).map(field => (
                <div key={field} className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontSize: '0.85rem' }}>
                    {field}{field === 'phone' && <span style={{ color: 'var(--red)' }}> *</span>}
                  </label>
                  <select value={mapping[field] ?? ''}
                    onChange={e => setMapping({ ...mapping, [field]: e.target.value ? parseInt(e.target.value) : undefined })}
                    disabled={submitting}
                  >
                    <option value="">— not mapped —</option>
                    {headers.map((h, i) => <option key={i} value={i}>{h}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <button onClick={clearFile} className="btn btn-secondary" disabled={submitting}
              style={{ padding: '6px 12px', fontSize: '0.85rem' }}>
              Remove file
            </button>
          </div>
        )}

        {/* Submit */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: '24px' }}>
          <button onClick={submit} disabled={submitting} className="btn btn-primary">
            {submitting ? 'Submitting...' : 'Run IMData (free)'}
          </button>
          {status && !error && (
            <span style={{ color: 'var(--green)', fontSize: '0.9rem' }}>{status}</span>
          )}
        </div>

        {error && (
          <div style={{
            marginTop: '16px', padding: '12px 16px', borderRadius: '8px',
            background: 'var(--red-bg)', color: 'var(--red)',
            border: '1px solid rgba(239,68,68,0.3)', fontSize: '0.9rem',
          }}>
            {error}
          </div>
        )}

        {resultJobId && (
          <div style={{ marginTop: '16px' }}>
            <Link to="/admin/verification-jobs" className="btn btn-secondary">
              View Verification Jobs →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
